import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import {
  AlertTriangle,
  TrendingUp,
  Eye,
  Activity,
  DollarSign,
  Users,
  Clock,
  CheckCircle2
} from 'lucide-react';
import { mockAMLAlerts, mockTransactionMonitoring } from '../mock/mockData';

const AMLMonitoring = () => {
  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-100 text-red-700 border-red-300',
      high: 'bg-orange-100 text-orange-700 border-orange-300',
      medium: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      low: 'bg-green-100 text-green-700 border-green-300'
    };
    return colors[severity] || 'bg-gray-100 text-gray-700';
  };

  const getStatusColor = (status) => {
    const colors = {
      blocked: 'bg-red-100 text-red-700',
      escalated: 'bg-orange-100 text-orange-700',
      'under-review': 'bg-blue-100 text-blue-700',
      investigating: 'bg-yellow-100 text-yellow-700',
      resolved: 'bg-green-100 text-green-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getRiskColor = (score) => {
    if (score >= 90) return 'text-red-600';
    if (score >= 75) return 'text-orange-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-green-600';
  };

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-8 py-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          AML Real-time Monitoring
        </h1>
        <p className="text-gray-600">
          Real-time alerts and red flagging based on behavioral, device monitoring, mule detection,
          sanctions, and digital transactions
        </p>
      </div>

      <div className="p-8 space-y-6">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-blue-600">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <Eye className="w-8 h-8 text-blue-600" />
                <Badge variant="secondary">+22%</Badge>
              </div>
              <p className="text-3xl font-bold mt-4">{mockTransactionMonitoring.totalMonitored}</p>
              <p className="text-sm text-gray-600 mt-1">Transactions Monitored</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-600">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <AlertTriangle className="w-8 h-8 text-orange-600" />
                <Badge className="bg-orange-100 text-orange-700">Live</Badge>
              </div>
              <p className="text-3xl font-bold mt-4">{mockTransactionMonitoring.realTimeAlerts}</p>
              <p className="text-sm text-gray-600 mt-1">Real-time Alerts</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-red-600">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <Activity className="w-8 h-8 text-red-600" />
                <Badge className="bg-red-100 text-red-700">Blocked</Badge>
              </div>
              <p className="text-3xl font-bold mt-4">{mockTransactionMonitoring.blockedTransactions}</p>
              <p className="text-sm text-gray-600 mt-1">Blocked Transactions</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-600">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <Clock className="w-8 h-8 text-green-600" />
                <Badge className="bg-green-100 text-green-700">Fast</Badge>
              </div>
              <p className="text-3xl font-bold mt-4">{mockTransactionMonitoring.avgProcessingTime}</p>
              <p className="text-sm text-gray-600 mt-1">Avg Processing Time</p>
            </CardContent>
          </Card>
        </div>

        {/* Real-time Alerts */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center space-x-2">
                <Activity className="w-5 h-5 text-red-600 animate-pulse" />
                <span>Live AML Alerts</span>
              </CardTitle>
              <div className="flex items-center space-x-2">
                <Badge className="bg-red-100 text-red-700">
                  {mockAMLAlerts.filter(a => a.severity === 'critical').length} Critical
                </Badge>
                <Badge className="bg-orange-100 text-orange-700">
                  {mockAMLAlerts.filter(a => a.severity === 'high').length} High
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {mockAMLAlerts.map((alert) => (
                <Card
                  key={alert.id}
                  className={`border-2 hover:shadow-lg transition-all ${
                    alert.severity === 'critical' ? 'border-red-300 bg-red-50' :
                    alert.severity === 'high' ? 'border-orange-300 bg-orange-50' :
                    'hover:border-blue-300'
                  }`}
                >
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {/* Alert Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <Badge variant="outline" className="font-mono text-xs">
                              {alert.alertId}
                            </Badge>
                            <Badge className={getSeverityColor(alert.severity)}>
                              {alert.severity.toUpperCase()}
                            </Badge>
                            <Badge className={getStatusColor(alert.status)}>
                              {alert.status.replace('-', ' ')}
                            </Badge>
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            {alert.type}
                          </h3>
                          <p className="text-sm text-gray-700 mb-3">{alert.description}</p>
                        </div>
                        <div className="flex flex-col items-end space-y-2">
                          <div className="text-right">
                            <p className={`text-2xl font-bold ${getRiskColor(alert.riskScore)}`}>
                              {alert.riskScore}
                            </p>
                            <p className="text-xs text-gray-600">Risk Score</p>
                          </div>
                        </div>
                      </div>

                      {/* Alert Details */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Customer/Account</p>
                          <p className="text-sm font-medium text-gray-900">{alert.customer}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Amount</p>
                          <p className="text-sm font-medium text-gray-900">{alert.amount}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Timestamp</p>
                          <p className="text-sm font-medium text-gray-900">{alert.timestamp}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Assigned To</p>
                          <p className="text-sm font-medium text-gray-900">{alert.assignee}</p>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center space-x-2 pt-4 border-t border-gray-200">
                        <Button size="sm" variant="outline">
                          View Details
                        </Button>
                        <Button size="sm" variant="outline">
                          Investigate
                        </Button>
                        {alert.status === 'under-review' && (
                          <Button size="sm" className="bg-red-600 hover:bg-red-700">
                            Escalate
                          </Button>
                        )}
                        {alert.status === 'investigating' && (
                          <Button size="sm" className="bg-green-600 hover:bg-green-700">
                            Resolve
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Alert Statistics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Alert Performance Metrics</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">True Positives Rate</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {Math.round((mockTransactionMonitoring.truePositives / (mockTransactionMonitoring.truePositives + mockTransactionMonitoring.falsePositives)) * 100)}%
                  </span>
                </div>
                <Progress value={22.3} className="h-2" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Alert Resolution Rate</span>
                  <span className="text-sm font-semibold text-gray-900">87%</span>
                </div>
                <Progress value={87} className="h-2" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Average Response Time</span>
                  <span className="text-sm font-semibold text-gray-900">4.2 hrs</span>
                </div>
                <Progress value={65} className="h-2" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Alert Categories Distribution</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Mule Account Detection</p>
                      <p className="text-xs text-gray-600">High risk pattern detected</p>
                    </div>
                    <Badge className="bg-red-100 text-red-700">34</Badge>
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-orange-50 border border-orange-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Behavioral Anomaly</p>
                      <p className="text-xs text-gray-600">Unusual transaction patterns</p>
                    </div>
                    <Badge className="bg-orange-100 text-orange-700">67</Badge>
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Sanctions Screening</p>
                      <p className="text-xs text-gray-600">Watchlist matches</p>
                    </div>
                    <Badge className="bg-blue-100 text-blue-700">23</Badge>
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Device Anomaly</p>
                      <p className="text-xs text-gray-600">Suspicious device activity</p>
                    </div>
                    <Badge className="bg-yellow-100 text-yellow-700">45</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AMLMonitoring;
